/**
 * Canonical structured payroll readiness issue (P1-A).
 *
 * Pure types + helpers. Downstream UI/gate rewiring is P1-B.
 */

export type PayrollReadinessIssueDomain =
  | 'PAY_CONTEXT'
  | 'BREAK'
  | 'EVIDENCE'
  | 'CALCULATION'
  | 'APPROVAL'
  | 'EXPORT';

export type PayrollReadinessRawSeverity = 'HARD' | 'SOFT';

/**
 * Stable machine codes emitted by summarisePayrollRunReadiness as structured issues.
 * Nested pay-context / break / fact codes from resolvers are passed through as-is when present.
 */
export const PAYROLL_RUN_READINESS_ISSUE_CODES = {
  NOT_APPROVED: 'NOT_APPROVED',
  MISSING_COMPLETED_TIME_SOURCE_EVIDENCE: 'MISSING_COMPLETED_TIME_SOURCE_EVIDENCE',
  AUTO_CLOSED_PENDING_CONFIRMATION: 'AUTO_CLOSED_PENDING_CONFIRMATION',
  IMPLAUSIBLE_WORKED_DURATION: 'IMPLAUSIBLE_WORKED_DURATION',
  POSSIBLE_DUPLICATE_TIMESHEET: 'POSSIBLE_DUPLICATE_TIMESHEET',
  AWARD_CLASSIFICATION_MISSING: 'AWARD_CLASSIFICATION_MISSING',
  UNSUPPORTED_AWARD: 'UNSUPPORTED_AWARD',
  AWARD_REVIEW_REQUIRED: 'AWARD_REVIEW_REQUIRED',
  ZERO_PAY: 'ZERO_PAY',
  OPEN_PAYROLL_DISPUTE: 'OPEN_PAYROLL_DISPUTE',
  FT_OVER_8H_WITHOUT_10H_AGREEMENT: 'FT_OVER_8H_WITHOUT_10H_AGREEMENT',
  PAY_CONTEXT_NOT_RESOLVED: 'PAY_CONTEXT_NOT_RESOLVED',
  BREAK_DURATION_UNRESOLVED: 'BREAK_DURATION_UNRESOLVED',
  BREAK_TREATMENT_BLOCKED: 'BREAK_TREATMENT_BLOCKED',
  BREAK_TREATMENT_NOT_RESOLVED: 'BREAK_TREATMENT_NOT_RESOLVED',
  BREAK_STATUS_UNKNOWN: 'BREAK_STATUS_UNKNOWN',
  BREAK_TREATMENT_FACTS_MISSING: 'BREAK_TREATMENT_FACTS_MISSING',
  PAY_EVENT_FACTS_NOT_READY: 'PAY_EVENT_FACTS_NOT_READY',
  PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING: 'PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING',
  /** Clock-in/out honesty failed (device clock, GPS, or paper-path integrity). */
  CLOCK_HONESTY: 'CLOCK_HONESTY',
  /** SAH/client service agreement missing or stale. */
  SERVICE_AGREEMENT_GAP: 'SERVICE_AGREEMENT_GAP',
  /** Care-plan acknowledgement or inclusion missing for the billed visit. */
  CARE_PLAN_GAP: 'CARE_PLAN_GAP',
  WORKED_TIME_PAYABLE_MISSING: 'WORKED_TIME_PAYABLE_MISSING',
  /** Nested free-form payload without a machine code. */
  PAY_CONTEXT_NESTED: 'PAY_CONTEXT_NESTED',
  BREAK_NESTED: 'BREAK_NESTED',
  PAY_FACT_NESTED: 'PAY_FACT_NESTED',
} as const;

export type PayrollRunReadinessIssueCode =
  (typeof PAYROLL_RUN_READINESS_ISSUE_CODES)[keyof typeof PAYROLL_RUN_READINESS_ISSUE_CODES];

/**
 * Codes the period aggregator (`aggregatePayrollPeriodReadiness`) emits on calculation.blockers.
 * Includes the typed PeriodReadinessBlockerCode union values.
 */
export const PERIOD_AGGREGATOR_BLOCKER_CODES = [
  'ASSIGNMENT_PAY_CONTEXT_BLOCKED',
  'ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED',
  'PAY_IDENTITY_CORRECTION_PENDING',
  'ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL',
  'ASSIGNMENT_NO_TIMESHEET',
  'ASSIGNMENT_COVERED_BY_APPROVED_LEAVE',
  'TIMESHEET_ZERO_PAY',
  'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED',
  'TIMESHEET_PAY_CONTEXT_STALE',
  'TIMESHEET_NOT_APPROVED',
  'TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY',
  'RATE_SCHEDULE_GAP',
  'WORKER_MISSING_PAY_PROFILE',
  'EBA_EXPORT_NOT_READY',
] as const;

export type PeriodAggregatorBlockerCode = (typeof PERIOD_AGGREGATOR_BLOCKER_CODES)[number];

/**
 * Known nested pay-context codes from resolvePayContextForEvent + PAY_CONTEXT_BLOCKER_LABELS.
 * Nested items are also accepted dynamically; this list seeds classification + descriptors.
 */
/** Exact pay-invalidation emitters in the pay-context spine and segment effects queue.
 * These reset pricing only; never classify arbitrary *_CHANGED codes by pattern.
 */
export const PAY_CONTEXT_RECALCULATION_CODES: readonly string[] = [
  'ASSIGNMENT_WORK_SEGMENT_CHANGED',
  'SOURCE_PAY_INPUT_CHANGED',
  'WORKER_PAY_IDENTITY_CHANGED',
  'ROSTER_ROLE_MAPPING_CHANGED',
  'TENANT_PAY_ROLE_CHANGED',
  'PAY_FACT_SET_CHANGED',
  'WORK_STATE_CONFIGURATION_CHANGED',
  'ROSTER_SLOT_PAY_INPUT_CHANGED',
  'ROSTER_SHIFT_PAY_INPUT_CHANGED',
] as const;

export const KNOWN_PAY_CONTEXT_NESTED_CODES = [
  'PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING',
  ...PAY_CONTEXT_RECALCULATION_CODES,
  'PAY_ROLE_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_STALE',
  'PAY_ROLE_NOT_FOUND',
  'PAY_ROLE_NOT_ACTIVE',
  'PAY_ROLE_ELIGIBILITY_BLOCKED',
  'PAY_ROLE_AMBIGUOUS',
  'PAY_ROLE_MISSING',
  'STAFF_REQUIRED',
  'STAFF_PAY_PROFILE_NOT_FOUND',
  'STAFF_PAY_PROFILE_AMBIGUOUS',
  'STAFF_CLASSIFICATION_NOT_FOUND',
  'RATE_NOT_FOUND',
  'RATE_CONTEXT_NOT_FOUND',
  'PAY_FACTS_MISSING',
  'PAY_FACT_SET_REQUIRED',
  'PAY_FACT_SET_NOT_APPROVED',
  'ALLOWANCE_RECOMPUTE_REQUIRED',
  'ALLOWANCE_PAY_CERTIFICATION_REQUIRED',
  'PAY_RULE_TWO_ORACLE_CERTIFICATION_REQUIRED',
  'PAY_CONTEXT_ORDINARY_PROFILE_DRIFT',
  'PAY_CONTEXT_RATE_IDENTITY_DRIFT',
  'PAY_CONTEXT_EBA_IDENTITY_DRIFT',
  'PAY_CONTEXT_RULE_IDENTITY_DRIFT',
  'SALARY_FLAT_REVIEW_REQUIRED',
  'SALARY_FLAT_CONFIG_INCOMPLETE',
  'CONTRACT_HOURLY_CONFIG_MISSING',
  'CONTRACT_HOURLY_RATE_REQUIRED',
  'CONTRACT_WEEKLY_HOURS_REQUIRED',
  'CONTRACT_OVERTIME_TERMS_REQUIRED',
  'CONTRACT_PENALTY_TERMS_REQUIRED',
  'CONTRACT_TERMS_EVIDENCE_REQUIRED',
  'CONTRACT_CONFIGURATION_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_NOT_APPROVED',
  'CONTRACT_HOURLY_TREATMENT_INVALID',
  'CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED',
] as const;

/** Export-gate codes that may appear on period export readiness (not calculation). */
export const KNOWN_EXPORT_BLOCKER_CODES = [
  'XERO_MAPPING_MISSING',
  'XERO_MAPPING_DRIFT',
  'DRY_RUN_NOT_RUN',
  'DRY_RUN_FAILED',
  'DRY_RUN_RECONCILIATION',
] as const;

/**
 * Open-ended export-readiness family from provider-responsibility-loader.
 * Code shape: PAYROLL_PROVIDER_RESPONSIBILITY_{responsibilityKind}_EVIDENCE_MISSING
 * Classified by prefix in blockerImpact (not a closed code list).
 */
export const PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX =
  'PAYROLL_PROVIDER_RESPONSIBILITY_' as const;
export const PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX = '_EVIDENCE_MISSING' as const;

/** Open-ended family: PAYROLL_PROVIDER_RESPONSIBILITY_{kind}_EVIDENCE_MISSING */
export function isProviderResponsibilityEvidenceCode(code: string): boolean {
  return (
    code.startsWith(PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX) &&
    code.endsWith(PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX) &&
    code.length >
      PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX.length +
        PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX.length
  );
}

/** Open-ended approved-leave readiness family from payroll-period-readiness-loader. */
export const APPROVED_LEAVE_READINESS_CODE_PREFIX = 'APPROVED_LEAVE_' as const;

/** APPROVED_LEAVE_* codes (PH payment evidence, unsupported treatment, handoff modes). */
export function isApprovedLeaveReadinessCode(code: string): boolean {
  return (
    code.startsWith(APPROVED_LEAVE_READINESS_CODE_PREFIX) &&
    code.length > APPROVED_LEAVE_READINESS_CODE_PREFIX.length
  );
}

/** Extract `{kind}` from PAYROLL_PROVIDER_RESPONSIBILITY_{kind}_EVIDENCE_MISSING. */
export function providerResponsibilityKindFromCode(code: string): string | null {
  if (!isProviderResponsibilityEvidenceCode(code)) return null;
  return code.slice(
    PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX.length,
    code.length - PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX.length,
  );
}

/** Build a provider-responsibility evidence code for a given kind. */
export function providerResponsibilityEvidenceCode(kind: string): string {
  return `${PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX}${kind}${PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX}`;
}

export interface PayrollReadinessIssue {
  domain: PayrollReadinessIssueDomain;
  /** Stable machine code (see enumeration / impact tests). */
  code: string;
  /** From resolver when available; HARD default for structural grid codes. */
  rawSeverity: PayrollReadinessRawSeverity;
  resourceType: string;
  resourceId: string;
  staffId?: string;
  /** Reserved for P2 remedy routing — do not consume in P1. */
  parameters?: Record<string, unknown>;
}

export interface NestedBlockerItem {
  code: string | null;
  message: string;
  severity: PayrollReadinessRawSeverity | null;
}

const SCREAMING_SNAKE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;

export function parseNestedBlockerItems(value: unknown): NestedBlockerItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item): NestedBlockerItem | null => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (!trimmed) return null;
        if (SCREAMING_SNAKE.test(trimmed)) {
          return { code: trimmed, message: trimmed, severity: null };
        }
        return { code: null, message: trimmed, severity: null };
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const codeRaw = record.code;
        const code =
          typeof codeRaw === 'string' && codeRaw.trim().length > 0 ? codeRaw.trim() : null;
        const messageRaw = record.message ?? record.reason ?? record.code ?? record.severity;
        const message =
          typeof messageRaw === 'string' && messageRaw.trim().length > 0
            ? messageRaw.trim()
            : (code ?? JSON.stringify(item));
        let severity: PayrollReadinessRawSeverity | null = null;
        const severityRaw = String(record.severity ?? '')
          .trim()
          .toUpperCase();
        if (severityRaw === 'HARD' || severityRaw === 'SOFT') severity = severityRaw;
        return { code, message, severity };
      }
      const asText = String(item ?? '').trim();
      if (!asText) return null;
      return { code: null, message: asText, severity: null };
    })
    .filter((item): item is NestedBlockerItem => Boolean(item));
}

export function issueFromFields(input: {
  domain: PayrollReadinessIssueDomain;
  code: string;
  rawSeverity: PayrollReadinessRawSeverity;
  resourceType: string;
  resourceId: string;
  staffId?: string | null;
  parameters?: Record<string, unknown>;
}): PayrollReadinessIssue {
  const issue: PayrollReadinessIssue = {
    domain: input.domain,
    code: input.code,
    rawSeverity: input.rawSeverity,
    resourceType: input.resourceType,
    resourceId: input.resourceId,
  };
  if (input.staffId) issue.staffId = input.staffId;
  if (input.parameters && Object.keys(input.parameters).length > 0) {
    issue.parameters = input.parameters;
  }
  return issue;
}

/**
 * Static seed of known readiness codes (nested labels + registries).
 *
 * CI enumeration for impact coverage must **not** rely on this alone —
 * use emitter source-scan (`discoverEmittedReadinessCodesFromSources`) so a
 * new literal in an emitter fails the test when unclassified.
 * This helper remains for descriptor seeding / representative fixtures.
 */
export function allEmittedReadinessCodes(): string[] {
  const codes = new Set<string>();
  for (const code of Object.values(PAYROLL_RUN_READINESS_ISSUE_CODES)) codes.add(code);
  for (const code of PERIOD_AGGREGATOR_BLOCKER_CODES) codes.add(code);
  for (const code of KNOWN_PAY_CONTEXT_NESTED_CODES) codes.add(code);
  for (const code of KNOWN_EXPORT_BLOCKER_CODES) codes.add(code);
  // Representative open-ended provider-responsibility family member
  codes.add(providerResponsibilityEvidenceCode('TAX'));
  return Array.from(codes).sort();
}
